import React, { useEffect, useState } from "react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
    ReferenceLine,
} from "recharts";
import { Tooltip as MuiTooltip } from "@mui/material";
import { getFairValueChart } from "../../../api/stocks_api";
import { getCurrencySymbol } from "../../../utils/currency";
import { fvAnalystTooltip } from "../../MainMenu/Portfolio/portfolioHelpers";
/* Fair-value builder + fair-value/analyst-estimate history (2026-08-10 APIs) */
import { FairValueBuilder } from "../Widgets/fairValueBuilder";
import { FairValueHistory } from "../Widgets/valuationHistory";

/* ── Theme-aware palette (CSS variables adapt to light/dark mode) ── */
const C = {
    blue: "var(--color-primary)",
    green: "var(--color-success)",
    red: "var(--color-error)",
    purple: "#8b5cf6",
    orange: "#f97316",
    ink: "var(--color-text-primary)",
    grey1: "var(--color-text-secondary)",
    grey2: "var(--color-text-muted)",
    grey3: "var(--color-border-secondary)",
    grey4: "var(--color-border-primary)",
    bg: "var(--color-surface)",
};

/* ── Formatters ── */
const money0 = (v, sym) =>
    v == null || isNaN(Number(v)) ? "—" : `${sym}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const money2 = (v, sym) =>
    v == null || isNaN(Number(v)) ? "—" : `${sym}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct0 = (v, signed = true) =>
    v == null || isNaN(Number(v)) ? "—" : `${signed && Number(v) >= 0 ? "+" : ""}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}%`;

/* ── Right/left-anchored line label with readable halo ── */
const LineLabel = ({ viewBox, text, color, side = "right", dy = 4, fontSize = 11, tooltip }) => {
    if (!viewBox) return null;
    const { x, y, width } = viewBox;
    const isRight = side === "right";
    const label = (
        <text
            x={isRight ? x + width + 4 : x + 4}
            y={y + dy}
            textAnchor="start"
            fontSize={fontSize}
            fontWeight={600}
            fill={color}
            style={{ paintOrder: "stroke", stroke: "var(--color-surface)", strokeWidth: 4, strokeOpacity: 0.8, pointerEvents: tooltip ? "auto" : "none", cursor: "default" }}
        >
            {text}
        </text>
    );
    if (!tooltip) return label;
    return (
        <MuiTooltip title={tooltip} placement="top" arrow>
            {label}
        </MuiTooltip>
    );
};

/* ── Unified hover tooltip ── */
const ChartTooltip = ({ active, payload, label, sym }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <div style={{ backgroundColor: C.bg, border: `1px solid ${C.grey3}`, borderRadius: 6, padding: "8px 10px", fontSize: 12 }}>
            <div style={{ color: C.ink, fontWeight: 700, marginBottom: 4 }}>
                {new Date(label).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
            </div>
            {payload.map((p) => (
                <div key={p.dataKey} style={{ color: p.stroke, fontWeight: 600 }}>
                    {p.name}: {money2(p.value, sym)}
                </div>
            ))}
        </div>
    );
};

/* ── Callout / banner strip ── */
const Banner = ({ kind, children }) => {
    const styles = {
        success: { bg: "rgba(12,163,12,0.1)", border: "rgba(12,163,12,0.35)", text: "var(--color-success)" },
        info: { bg: "rgba(42,120,214,0.1)", border: "rgba(42,120,214,0.35)", text: "var(--color-info)" },
        warning: { bg: "rgba(250,178,25,0.12)", border: "rgba(250,178,25,0.45)", text: "var(--color-warning)" },
        error: { bg: "rgba(208,59,59,0.12)", border: "rgba(208,59,59,0.4)", text: "var(--color-error)" },
    }[kind];
    return (
        <div style={{
            backgroundColor: styles.bg, border: `1px solid ${styles.border}`, color: styles.text,
            borderRadius: "var(--radius-md)", padding: "10px 14px", fontSize: "var(--font-size-sm)",
            fontWeight: 500, lineHeight: 1.55, marginBottom: "var(--spacing-sm)",
        }}>
            {children}
        </div>
    );
};

const FairValue = ({ stock }) => {
    const ticker = stock?.ticker;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const sym = getCurrencySymbol();

    useEffect(() => {
        if (!ticker) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(false);
            const res = await getFairValueChart(ticker);
            if (cancelled) return;
            if (res?.success) setData(res.data);
            else setError(true);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [ticker]);

    if (loading) {
        return (
            <div style={{ padding: "var(--spacing-lg)" }}>
                <div className="animate-pulse" style={{ height: 90, backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)", marginBottom: 16 }} />
                <div className="animate-pulse" style={{ height: 520, backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)" }} />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div style={{ padding: "var(--spacing-2xl)", textAlign: "center", color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)" }}>
                Fair value data is not available for {ticker}.
            </div>
        );
    }

    const {
        company_name, sector_name, fair_value,
        fv_analyst, fv_iv, band, price, upside_to_fv, signal,
        quality_ok, rsi14, analyst_consensus,
        prices = [],
    } = data;

    /* ── §2.5 Y-axis range — never autoscale, never clip levels ── */
    const closes = prices.map((p) => p.close).filter((v) => v != null);
    const refs = [fair_value, fv_analyst].filter((v) => v != null);
    const lo = Math.min(...(closes.length ? closes : [0]), ...(refs.length ? refs : [Infinity]));
    const hi = Math.max(...(closes.length ? closes : [0]), ...(refs.length ? refs : [-Infinity]));
    const pad = (hi - lo) * 0.08 || hi * 0.08 || 1;
    const yMin = lo - pad;
    const yMax = hi + pad;

    const hasFV = fair_value != null;
    const tickInterval = Math.max(1, Math.floor(prices.length / 8));

    /* ── Metric tiles (§4) — Signal tile strips any " (…)" suffix ── */
    const tiles = [
        { label: "Price", value: money2(price, sym) },
        { label: "Fair value", value: money2(fair_value, sym) },
        { label: "FV TV", value: money2(fv_iv, sym) },
        /* The analyst anchor feeds the fair-value mean alongside FV IV — surfaced as a
           tile here, not just as a chart reference line. (Own-history FV removed 2026-10-09.) */
        ...(fv_analyst != null ? [{ label: "FV analyst", value: money2(fv_analyst, sym) }] : []),
        { label: "Upside to FV", value: pct0(upside_to_fv) },
        { label: "Signal", value: signal ? signal.replace(/\s*\(.*\)\s*$/, "") : "—" },
        { label: "Band width", value: band != null ? `${Math.round(Number(band) * 100)}%` : "—" },
        { label: "RSI-14", value: rsi14 != null ? Math.round(Number(rsi14)) : "—" },
    ];

    return (
        <div style={{ padding: "0 var(--spacing-lg) var(--spacing-2xl)" }}>

            {/* Street grade consensus caption */}
            {analyst_consensus != null && (
                <div style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)", marginBottom: "var(--spacing-sm)" }}>
                    Street grade consensus: <strong style={{ color: "var(--color-text-primary)" }}>{analyst_consensus}</strong>
                </div>
            )}

            {/* Metric tiles */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "var(--spacing-sm)", marginBottom: "var(--spacing-md)" }}>
                {tiles.map((t) => (
                    <div key={t.label} style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)", padding: "12px 14px", minWidth: 0 }}>
                        <div style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)", fontWeight: 500, marginBottom: 6, whiteSpace: "nowrap" }}>{t.label}</div>
                        <div style={{ color: "var(--color-text-primary)", fontSize: "var(--font-size-xl)", fontWeight: "var(--font-weight-bold)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={String(t.value)}>
                            {t.value}
                        </div>
                    </div>
                ))}
            </div>

            {/* Chart card */}
            <div style={{ backgroundColor: C.bg, border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)", padding: "var(--spacing-md)", overflow: "hidden" }}>
                <div style={{ color: C.ink, fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
                    {ticker} — {company_name}{sector_name ? ` (${sector_name})` : ""}
                </div>

                <ResponsiveContainer width="100%" height={520}>
                    <LineChart data={prices} margin={{ top: 48, right: 150, bottom: 44, left: 10 }}>
                        <CartesianGrid stroke={C.grey4} strokeWidth={1} vertical={false} />

                        <XAxis
                            dataKey="date"
                            interval={tickInterval}
                            axisLine={{ stroke: C.grey3 }}
                            tickLine={false}
                            tick={{ fill: C.grey2, fontSize: 11 }}
                            tickFormatter={(d) => new Date(d).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                        />
                        <YAxis
                            domain={[yMin, yMax]}
                            allowDataOverflow
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: C.grey2, fontSize: 11 }}
                            tickFormatter={(v) => money0(v, sym)}
                            width={60}
                        />

                        {/* §2.4 context anchors — dotted, dimmer, right-anchored like thresholds */}
                        {fv_analyst != null && (
                            <ReferenceLine
                                y={fv_analyst} stroke={C.purple} strokeWidth={1} strokeDasharray="2 3"
                                label={<LineLabel text={`Analyst target ${money0(fv_analyst, sym)}`} color={C.purple} dy={-6} fontSize={10} tooltip={fvAnalystTooltip(fv_analyst, data.num_target_analysts_3m, data.num_target_analysts_12m)} />}
                            />
                        )}

                        {/* §2.3 fair-value line (entry/exit removed 2026-10-09) */}
                        {hasFV && (
                            <ReferenceLine
                                y={fair_value} stroke={C.ink} strokeWidth={1.2} strokeDasharray="6 4"
                                label={<LineLabel text={`Fair value ${money0(fair_value, sym)}`} color={C.ink} dy={4} />}
                            />
                        )}

                        {/* §2.2 series */}
                        <Line type="linear" dataKey="close" name="Close" stroke={C.blue} strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
                        <Line type="linear" dataKey="dma50" name="50-DMA" stroke={C.grey1} strokeWidth={1} strokeDasharray="5 4" dot={false} connectNulls={false} isAnimationActive={false} />
                        <Line type="linear" dataKey="dma200" name="200-DMA" stroke={C.grey2} strokeWidth={1} strokeDasharray="5 4" dot={false} connectNulls={false} isAnimationActive={false} />

                        <RechartsTooltip content={<ChartTooltip sym={sym} />} />
                        <Legend
                            verticalAlign="bottom"
                            wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                            payload={[
                                { value: "Close", type: "line", color: C.blue },
                                { value: "50-DMA", type: "line", color: C.grey1 },
                                { value: "200-DMA", type: "line", color: C.grey2 },
                            ]}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Conditional banners below the chart (§4, in order) */}
            <div style={{ marginTop: "var(--spacing-md)" }}>
                {signal === "N/A (data quality issue)" ? (
                    <Banner kind="error">
                        <strong>Data quality issue</strong> — this name's source price is known-corrupted; every number shown here is untrustworthy. Do not act on this data.
                    </Banner>
                ) : signal?.startsWith("N/A") ? (
                    <Banner kind="info">
                        No positive earnings anchor is available for this stock, so no fair-value signal can be computed.
                    </Banner>
                ) : null}

                {quality_ok === false && (
                    <Banner kind="warning">
                        <strong>Quality warning</strong> — this stock fails the factor-model quality gate; value signals carry value-trap risk.
                    </Banner>
                )}
            </div>

            {/* ── Custom fair value from caller-selected anchors ── */}
            <FairValueBuilder ticker={ticker} />

            {/* ── Dated fair values with the analyst estimates that feed them ── */}
            <FairValueHistory ticker={ticker} />
        </div>
    );
};

export default FairValue;
